import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { Form } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaGuideStatusCreate,
  schemaGuideStatusEdit,
} from '../schemas/yup.guide-status.schema';
import { IGuideStatus } from '../../../shared/interfaces';
import { patchGuideStatus, postGuideStatus } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface IGuideStatusProps {
  open: boolean;
  close: () => void;
  guideStatus: IGuideStatus | null;
  setGuidesStatus: React.Dispatch<React.SetStateAction<IGuideStatus[]>>;
}

const GuideStatusCreateEdit: React.FC<IGuideStatusProps> = ({
  open,
  close,
  guideStatus,
  setGuidesStatus,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [color, setColor] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaGuideStatusCreate
      .validate({
        description,
        color,
      })
      .then(async (validated) => {
        await postGuideStatus(validated)
          .then((data) => {
            setGuidesStatus((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('guidesStatusModal.toast.successTitle'),
              description: t('guidesStatusModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('guidesStatusModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('guidesStatusModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('guidesStatusModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, color, status, t, addToast, close, setGuidesStatus]);

  const handleEdit = useCallback(async () => {
    schemaGuideStatusEdit
      .validate({
        description,
        color,
        status,
      })
      .then(async (validated) => {
        await patchGuideStatus(guideStatus?.id, validated)
          .then((data) => {
            setGuidesStatus((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('guidesStatusModal.toast.infoTitle'),
              description: t('guidesStatusModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('guidesStatusModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('guidesStatusModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('guidesStatusModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    addToast,
    close,
    color,
    description,
    guideStatus?.id,
    setGuidesStatus,
    status,
    t,
  ]);

  useEffect(() => {
    if (guideStatus) {
      setDescription(guideStatus.description);
      setColor(guideStatus.color);
      setStatus(guideStatus.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        guideStatus ? (
          <>
            <EditOutlined /> {t('guidesStatusModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('guidesStatusModal.titleCreate')}
          </>
        )
      }
      onOk={guideStatus ? handleEdit : handleCreate}
    >
      <Form
        layout="vertical"
        initialValues={{ layout: guideStatus }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item label={t('guidesStatusModal.fields.description')}>
          <AInput
            placeholder={t('guidesStatusModal.fields.descriptionPlaceholder')}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('guidesStatusModal.fields.color')}>
          <AInput
            type="color"
            placeholder={t('guidesStatusModal.fields.colorPlaceholder')}
            name="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </Form.Item>
        {guideStatus && (
          <Form.Item label={t('guidesStatusModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default GuideStatusCreateEdit;
